import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Save, KeyRound, Copy, Check, Link as LinkIcon, Phone, Building2, Smartphone, Globe } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../utils/api';
import GoogleSheetsSyncCard from '../components/GoogleSheetsSyncCard';

const SECTIONS = [
  {
    title: 'URLs',
    icon: Globe,
    fields: [
      { key: 'frontend_url', label: 'Frontend / Web App URL', placeholder: 'https://your-clinic.com', help: 'Base URL used in WhatsApp action links and patient portal links.' },
      { key: 'patient_portal_url', label: 'Patient Portal URL', placeholder: 'https://your-clinic.com/patient-portal', help: 'Direct link shared with patients to log in via web.' },
    ],
  },
  {
    title: 'Clinic Info',
    icon: Building2,
    fields: [
      { key: 'clinic_name', label: 'Clinic Name', placeholder: 'Eden Skin Clinic' },
      { key: 'clinic_phone', label: 'Support Phone', placeholder: '+917710117777' },
      { key: 'clinic_gstin', label: 'GSTIN (printed on receipts)', placeholder: '03XXXXX0000X0ZX' },
      { key: 'clinic_address', label: 'Clinic Address', placeholder: '123 Medical Street, City' },
      { key: 'clinic_gstin', label: 'GSTIN (printed on receipts)', placeholder: '03XXXXX0000X0ZX' },
    ],
  },
  {
    title: 'Mobile App Store Links',
    icon: Smartphone,
    fields: [
      { key: 'android_store_url', label: 'Android Play Store URL', placeholder: 'https://play.google.com/store/apps/details?id=...' },
      { key: 'ios_store_url', label: 'iOS App Store URL', placeholder: 'https://apps.apple.com/app/...' },
    ],
  },
];

export default function SettingsPage({ user, onLogout }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // OTP generator state
  const [otpPhone, setOtpPhone] = useState('');
  const [otpGenerating, setOtpGenerating] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/app-settings');
        setSettings(res.data || {});
      } catch (err) {
        toast.error('Could not load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      SECTIONS.forEach((sec) => sec.fields.forEach((f) => { payload[f.key] = settings[f.key] || ''; }));
      const res = await api.put('/admin/app-settings', payload);
      setSettings(res.data || {});
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cleanPhone = (val) => val.replace(/\D/g, '').slice(-10);

  const handleGenerateOtp = async () => {
    const p = cleanPhone(otpPhone);
    if (p.length !== 10) {
      toast.error('Enter a valid 10-digit phone');
      return;
    }
    setOtpGenerating(true);
    setGeneratedOtp(null);
    setCopied(false);
    try {
      const res = await api.post('/patient-portal/admin/generate-otp', { phone: p });
      setGeneratedOtp(res.data);
      toast.success('OTP generated. Share it with the patient.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate OTP');
    } finally {
      setOtpGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedOtp?.otp) return;
    try {
      await navigator.clipboard.writeText(generatedOtp.otp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <header>
          <h1 className="text-2xl font-bold text-slate-900">App Settings</h1>
          <p className="text-slate-500 text-sm mt-1">URLs, clinic info, and store links shared with the patient mobile app.</p>
        </header>

        {/* Patient OTP Generator - prominent at top */}
        <Card className="p-6 rounded-2xl border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="settings-otp-generator">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Generate Patient OTP (fallback)</h2>
              <p className="text-sm text-slate-600">Use this when 2Factor SMS doesn't reach the patient. Works for both the web portal and the mobile app. Expires in 5 minutes.</p>
            </div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm">Patient phone (10 digits)</Label>
              <div className="flex items-center gap-2 rounded-xl border border-slate-300 focus-within:border-emerald-500 px-3 py-1 mt-1">
                <Phone className="w-4 h-4 text-slate-400" />
                <span className="text-slate-500 text-sm">+91</span>
                <Input
                  value={otpPhone}
                  onChange={(e) => setOtpPhone(cleanPhone(e.target.value))}
                  placeholder="98XXXXXXXX"
                  maxLength={10}
                  className="border-0 focus-visible:ring-0 px-0"
                  data-testid="settings-otp-phone-input"
                />
              </div>
            </div>
            <Button
              onClick={handleGenerateOtp}
              disabled={otpGenerating || otpPhone.length !== 10}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
              data-testid="settings-otp-generate-btn"
            >
              {otpGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate OTP'}
            </Button>
          </div>

          {generatedOtp && (
            <div className="mt-5 rounded-xl border-2 border-dashed border-emerald-300 bg-white p-5 flex items-center justify-between gap-4" data-testid="settings-otp-result">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">OTP for +91 {generatedOtp.phone}</p>
                <p className="text-3xl font-mono font-bold text-emerald-700 tracking-[0.3em] mt-1" data-testid="settings-otp-value">
                  {generatedOtp.otp}
                </p>
                <p className="text-xs text-slate-500 mt-1">{generatedOtp.note}</p>
              </div>
              <Button
                variant="outline"
                onClick={handleCopy}
                className="rounded-xl"
                data-testid="settings-otp-copy-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
              </Button>
            </div>
          )}
        </Card>

        {/* Settings sections */}
        {SECTIONS.map((sec) => {
          const Icon = sec.icon;
          return (
            <Card key={sec.title} className="p-6 rounded-2xl border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-slate-600" />
                <h3 className="font-semibold text-slate-900">{sec.title}</h3>
              </div>
              <div className="space-y-4">
                {sec.fields.map((f) => (
                  <div key={f.key}>
                    <Label htmlFor={f.key} className="text-sm">{f.label}</Label>
                    <Input
                      id={f.key}
                      value={settings[f.key] ?? ''}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="mt-1 rounded-xl"
                      data-testid={`settings-input-${f.key}`}
                    />
                    {f.help && <p className="text-xs text-slate-500 mt-1">{f.help}</p>}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}

        <div className="flex justify-end sticky bottom-4">
          <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg" data-testid="settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save settings
          </Button>
        </div>

        {/* Read-only mobile-app handoff info */}
        <Card className="p-5 rounded-2xl border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Mobile App API Handoff</h3>
          </div>
          <p className="text-xs text-slate-600 mb-2">Share these with the mobile-app team. The API key lives in backend <code className="bg-slate-200 px-1 rounded">.env</code> as <code className="bg-slate-200 px-1 rounded">PATIENT_APP_API_KEY</code>.</p>
          <ul className="text-sm text-slate-700 space-y-1">
            <li><strong>Backend base URL:</strong> set on the device build (e.g. {settings.frontend_url || '—'} replace path).</li>
            <li><strong>API namespace:</strong> <code>/api/patient/*</code></li>
            <li><strong>Required header on every request:</strong> <code>X-Patient-API-Key</code></li>
            <li><strong>Auth header after OTP verify:</strong> <code>Authorization: Bearer &lt;session_token&gt;</code></li>
          </ul>
        </Card>

        <GoogleSheetsSyncCard />
      </div>
    </Layout>
  );
}
